import { ZELTY, HUBRISE, BIBORNE, SPLASH, ONETOUCH, ADDITION, INGREDIENT } from 'src/constants';
import store from './store';

interface AccessOptions {
  cannotEditChoice: boolean;
  hideCreateCategory: boolean;
}
interface OptionAccessOptions {
  disableCheckbox: boolean;
  disableRefs: boolean;
  disablePrice: boolean;
  hideCreateOption: boolean;
  hasOptionFree: boolean;
}
interface OrderAccessOptions {
  hideprintTicket: boolean;
  cannotRefundOrder: boolean;
}
interface PaymentTypeAccessOptions {
  displayCounterType: boolean;
}

interface ProductsAccessOptions {
  disableOptionList: boolean;
  disableRefs: boolean;
  disablePrice: boolean;
  hideCreateProduct: boolean;
  pullCart: boolean;
  hideCreateSection: boolean;
  displayMenu?: boolean;
  hasMenuPOS: boolean;
}

interface ExpandablePanelAccessOptions {
  disableDuplication: boolean;
  disableDeletion: boolean;
}
interface PromoCodeAccessOptions {
  optionalRef?: boolean;
  obligatoryRef?: boolean;
}

interface Access {
  category: AccessOptions;
  option: OptionAccessOptions;
  order: OrderAccessOptions;
  paymentType: PaymentTypeAccessOptions;
  products: ProductsAccessOptions;
  expandablePanel: ExpandablePanelAccessOptions;
  promoCode: PromoCodeAccessOptions;
}

const DEFAULT_ACCESS: Access = {
  category: {
    cannotEditChoice: false,
    hideCreateCategory: false
  },
  option: {
    disableCheckbox: false,
    disableRefs: false,
    disablePrice: false,
    hideCreateOption: false,
    hasOptionFree: true
  },
  order: {
    hideprintTicket: false,
    cannotRefundOrder: false
  },
  paymentType: {
    displayCounterType: true,
  },
  products: {
    disableRefs: false,
    disablePrice: false,
    pullCart: false,
    disableOptionList: false,
    hideCreateProduct: false,
    hideCreateSection: false,
    hasMenuPOS: false,
    displayMenu: true,
  },
  expandablePanel: {
    disableDuplication: false,
    disableDeletion: false,
  },
  promoCode: {
    optionalRef: false,
  }
};

export const HUBRISE_ACCESS: Access = {
  ...DEFAULT_ACCESS,
  promoCode: {
    obligatoryRef: true
  }
};

export const ZELTY_ACCESS: Access = {
  ...DEFAULT_ACCESS,
  category: {
    ...DEFAULT_ACCESS.category,
    cannotEditChoice: true,
    hideCreateCategory: true
  },
  products: {
    ...DEFAULT_ACCESS.products,
    disableOptionList: true,
    hideCreateProduct: true,
    hideCreateSection: true,
    displayMenu: false,
    disablePrice: true,
    pullCart: true,
    disableRefs: true
  },
  option: {
    ...DEFAULT_ACCESS.option,
    disableCheckbox: true,
    hideCreateOption: true,
    disablePrice: true,
    disableRefs: true,
    hasOptionFree: false
  },
  expandablePanel: {
    disableDuplication: true,
    disableDeletion: true,
  },
  promoCode: {
    optionalRef: true
  }
};

export const BIBORNE_ACCESS: Access = {
  ...DEFAULT_ACCESS,
  order: {
    ...DEFAULT_ACCESS.order,
    hideprintTicket: true,
  },
  option: {
    ...DEFAULT_ACCESS.option,
    disableRefs: true
  },
  products: {
    ...DEFAULT_ACCESS.products,
    hasMenuPOS: true,
    pullCart: true,
    disableRefs: true
  },
  promoCode: {
    optionalRef: true,
  }
};

export const SPLASH_ACCESS: Access = {
  ...DEFAULT_ACCESS,
  products: {
    ...DEFAULT_ACCESS.products,
    hasMenuPOS: true,
    pullCart: true,
    disableRefs: true
  },
  option: {
    ...DEFAULT_ACCESS.option,
    disableRefs: true,
    hasOptionFree: false
  }
};

export const ONETOUCH_ACCESS: Access = {
  ...DEFAULT_ACCESS,
  order: {
    ...DEFAULT_ACCESS.order,
    cannotRefundOrder: true,
  }
};

export const ADDITION_ACCESS: Access = {
  ...DEFAULT_ACCESS,
  products: {
    ...DEFAULT_ACCESS.products,
    hasMenuPOS: true,
    pullCart: true,
    disableRefs: true
  },
  option: {
    ...DEFAULT_ACCESS.option,
    hideCreateOption: false,
    disableRefs: true
  },
  promoCode: {
    optionalRef: true,
  },
  order: {
    ...DEFAULT_ACCESS.order
  }
};

export const hasExternalIntegration = (shopId: string) => {
  const { shop = {}, shops = {} } = store.getState().firebaseRealTime;
  const { isAllShops } = store.getState().user
  const selectedShop = shopId || isAllShops ? shops[shopId] : shop
  return selectedShop?.configuration?.externalIntegration?.enabled ? selectedShop?.configuration?.externalIntegration?.type : false;
}

export const checkIntegrationAccess = (type: keyof Access, access: keyof AccessOptions, shopId: string) => {
  const { shop = {}, shops = {} } = store.getState().firebaseRealTime;
  const selectedShop = shopId ? shops[shopId] : shop;
  const { withoutMenuIntegrated } = selectedShop?.configuration?.externalIntegration?.configuration || false;

  const externalIntegration = hasExternalIntegration(shopId);

  let allow = false;

  switch (externalIntegration) {
    case HUBRISE:
      if (HUBRISE_ACCESS[type]) {
        allow = HUBRISE_ACCESS[type][access];
      }
      break;
    case ZELTY:
      if (ZELTY_ACCESS[type]) {
        allow = ZELTY_ACCESS[type][access];
      }
      break;
    case BIBORNE:
      if (!withoutMenuIntegrated && BIBORNE_ACCESS[type]) {
        allow = BIBORNE_ACCESS[type][access];
      } else {
        allow = { ...DEFAULT_ACCESS, order: { ...DEFAULT_ACCESS.order, hidePrintTicket: true } }[type][access]
      }
      break;
    case SPLASH:
      if (SPLASH_ACCESS[type]) {
        allow = SPLASH_ACCESS[type][access];
      }
      break;
    case ONETOUCH:
      if (ONETOUCH_ACCESS[type]) {
        allow = ONETOUCH_ACCESS[type][access];
      }
      break;
    case ADDITION:
      if (ADDITION_ACCESS[type]) {
        allow = ADDITION_ACCESS[type][access];
      }
      break;
    default:
      if (DEFAULT_ACCESS[type]) {
        allow = DEFAULT_ACCESS[type][access];
      }
      break;
  }

  return allow;
};


export const getSplashType = (type: string, shopId: string) => {
  return hasExternalIntegration(shopId) === SPLASH ? (type ? type : INGREDIENT) : null;
};